import React, { useCallback, useEffect, useState } from "react";
import {
  fetchAiHiringPlanStatus,
  fetchAiCallAssistStatus,
  createAiHiringFullBundleCheckoutSession,
} from "../../utils/api";
// Reuses the shared .ai-plan-price / .aca-plan-price-suffix / .aca-plan-breakdown
// styles already defined for the AI Call Assist / Agent247 product cards.
import "../aiCallAssist/AICallAssistPlanSelector.css";

// Copy only — the pricing tier itself always comes from the server
// (Agency.aiHiringPlan, Super-Admin controlled via
// PATCH /api/admin/agencies/:id/ai-hiring-membership). Exactly one of these
// two is ever shown, mirroring Agent247BillingCard's Member/Non-Member gate.
// Prices match the existing (unchanged) PLAN_TYPES amounts in
// src/routes/aiHiringPlan.routes.js — these are inline Stripe price_data,
// not persisted Stripe Price objects, so there is no live price to fetch
// (same precedent as the plain Member/Non-Member cards this replaces).
const TIER_COPY = {
  member: {
    title: "AI Hiring — Member Plan",
    price: "$399",
  },
  non_member: {
    title: "AI Hiring — Non-Member Plan",
    price: "$899",
  },
};

const BREAKDOWN = "$2,500 one-time platform access fee + $50/mo data usage fee";

const AIHiringFullBundleCard = ({ locationId }) => {
  const [loading, setLoading] = useState(true);
  const [planStatus, setPlanStatus] = useState(null);
  const [callAssistStatus, setCallAssistStatus] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [planPayload, callAssistPayload] = await Promise.all([
        fetchAiHiringPlanStatus({ locationId }),
        fetchAiCallAssistStatus({ locationId }),
      ]);
      setPlanStatus(planPayload);
      setCallAssistStatus(callAssistPayload);
      setError("");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load AI Hiring billing status.",
      );
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search || "");
    if (!params.get("ai_hiring_bundle_success")) return;

    setSuccess("Payment successful! Your AI Hiring bundle is now active.");
    void loadStatus();

    params.delete("ai_hiring_bundle_success");
    const nextQuery = params.toString();
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}`,
    );
  }, [loadStatus]);

  const handleSubscribe = async () => {
    setPending(true);
    setError("");
    try {
      const result = await createAiHiringFullBundleCheckoutSession({ locationId });
      const checkoutUrl = String(result?.url || "").trim();
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      throw new Error("Checkout session did not return a redirect URL.");
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to start checkout.",
      );
    } finally {
      setPending(false);
    }
  };

  // Hide entirely while loading, or if the agency has never been enabled for
  // this section — same gate the checkout route itself enforces
  // (Agency.aiCallAssistPaymentEnabled), so the card never appears able to
  // be clicked only to 403 on submit.
  if (loading) return null;
  if (!callAssistStatus?.paymentEnabled) return null;

  const tier = planStatus?.planType === "member" ? "member" : "non_member";
  const tierIsConfigured = Boolean(planStatus?.planType);
  const copy = TIER_COPY[tier];
  const isActive = Boolean(planStatus?.planActive);

  const activeNotes = [
    isActive ? "AI Hiring Plan ✓" : "",
    callAssistStatus?.platformAccess?.purchased ? "Platform Access ✓" : "",
    callAssistStatus?.dataUsage?.active ? "Data Usage ✓" : "",
  ].filter(Boolean);

  return (
    <section className="pb-section">
      <h2 className="pb-section-title">AI Hiring</h2>
      <div className="ai-plan-selector-wrap">
        {success ? <div className="ai-alert ai-alert-success">{success}</div> : null}
        {error ? <div className="ai-alert ai-alert-error">{error}</div> : null}

        <div className="ai-card ai-plan-selector-card">
          <div className="ai-card-header">
            <div>
              <p className="ai-card-note">
                Billing tier: {tierIsConfigured ? (tier === "member" ? "Member" : "Non-Member") : "Not yet configured"}
              </p>
              <h3 className="ai-card-title">{tierIsConfigured ? copy.title : "AI Hiring"}</h3>
            </div>
          </div>

          <div className="ai-plan-item">
            {tierIsConfigured ? (
              <>
                <span className="ai-plan-badge">plan + setup</span>
                <p className="ai-plan-price">{copy.price}</p>
                <p className="aca-plan-breakdown">+ {BREAKDOWN}</p>
                <p className="ai-help">
                  Full AI Hiring platform access, Platform Access, and Data Usage — billed
                  together via one Stripe Checkout.
                </p>
                {activeNotes.length ? (
                  <p className="ai-help">Currently active: {activeNotes.join(" · ")}</p>
                ) : null}

                {isActive ? (
                  <button type="button" className="ai-button ai-button-disabled" disabled>
                    ✓ Active
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ai-button ai-button-primary"
                    onClick={handleSubscribe}
                    disabled={pending}
                  >
                    {pending ? (
                      <>
                        <span className="ai-spinner" aria-hidden="true" />
                        Processing...
                      </>
                    ) : (
                      "Subscribe"
                    )}
                  </button>
                )}
              </>
            ) : (
              <p className="ai-help">
                Your AI Hiring billing tier has not been configured yet. Please contact
                support to get started.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIHiringFullBundleCard;
